import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import StopwatchTimer from './components/StopwatchTimer';
import ManualTimeEntry from './components/ManualTimeEntry';
import TimeEntriesList from './components/TimeEntriesList';
import TimeEntrySummary from './components/TimeEntrySummary';

const TimeTrackingDashboard = () => {
  const { user, profile } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]);
  const [lawyerRate, setLawyerRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('timer');
  const [filterCase, setFilterCase] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchTimeEntries = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      let query = supabase?.from('time_entries')?.select('*')?.eq('lawyer_id', user?.id)?.order('created_at', { ascending: false });

      if (filterCase) query = query?.ilike('case_name', `%${filterCase}%`);
      if (filterStatus) query = query?.eq('entry_status', filterStatus);
      if (filterDate) {
        const start = new Date(filterDate);
        const end = new Date(filterDate);
        end?.setDate(end?.getDate() + 1);
        query = query?.gte('started_at', start?.toISOString())?.lt('started_at', end?.toISOString());
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setTimeEntries(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load time entries');
    } finally {
      setLoading(false);
    }
  }, [user?.id, filterCase, filterDate, filterStatus]);

  const fetchLawyerRate = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error: rateError } = await supabase?.from('lawyer_rates')?.select('*')?.eq('lawyer_id', user?.id)?.eq('approval_status', 'approved')?.is('end_date', null)?.order('effective_date', { ascending: false })?.limit(1)?.single();
      if (!rateError && data) setLawyerRate(data);
    } catch (err) {
      // Rate may not exist yet
    }
  }, [user?.id]);

  useEffect(() => {
    fetchTimeEntries();
    fetchLawyerRate();
  }, [fetchTimeEntries, fetchLawyerRate]);

  const handleTimerSave = async (timerData) => {
    try {
      const { error: insertError } = await supabase?.from('time_entries')?.insert({
        lawyer_id: user?.id,
        case_id: timerData?.caseId || null,
        case_name: timerData?.caseName || null,
        duration_minutes: timerData?.durationMinutes,
        hourly_rate: timerData?.hourlyRate || lawyerRate?.base_hourly_rate || 0,
        billable_flag: timerData?.billable,
        entry_status: 'draft',
        activity_description: timerData?.activityDescription,
        started_at: timerData?.startedAt,
        ended_at: timerData?.endedAt,
        is_timer_entry: true
      });
      if (insertError) throw insertError;
      setSuccessMessage('Time entry saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchTimeEntries();
    } catch (err) {
      setError(err?.message || 'Failed to save time entry');
    }
  };

  const handleManualSave = async (entryData) => {
    try {
      if (editingEntry) {
        if (editingEntry?.entry_status !== 'draft') {
          setError('Cannot edit invoiced or paid entries');
          return;
        }
        const { error: updateError } = await supabase?.from('time_entries')?.update({
            case_id: entryData?.caseId || null,
            case_name: entryData?.caseName || null,
            duration_minutes: entryData?.durationMinutes,
            hourly_rate: entryData?.hourlyRate,
            billable_flag: entryData?.billable,
            activity_description: entryData?.activityDescription,
            started_at: entryData?.startedAt,
            ended_at: entryData?.endedAt
          })?.eq('id', editingEntry?.id)?.eq('entry_status', 'draft');
        if (updateError) throw updateError;
        setEditingEntry(null);
        setSuccessMessage('Time entry updated successfully!');
      } else {
        const { error: insertError } = await supabase?.from('time_entries')?.insert({
          lawyer_id: user?.id,
          case_id: entryData?.caseId || null,
          case_name: entryData?.caseName || null,
          duration_minutes: entryData?.durationMinutes,
          hourly_rate: entryData?.hourlyRate || lawyerRate?.base_hourly_rate || 0,
          billable_flag: entryData?.billable,
          entry_status: 'draft',
          activity_description: entryData?.activityDescription,
          started_at: entryData?.startedAt,
          ended_at: entryData?.endedAt,
          is_timer_entry: false
        });
        if (insertError) throw insertError;
        setSuccessMessage('Time entry created successfully!');
      }
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchTimeEntries();
      setActiveTab('entries');
    } catch (err) {
      setError(err?.message || 'Failed to save time entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const entry = timeEntries?.find(e => e?.id === entryId);
      if (entry?.entry_status !== 'draft') {
        setError('Only draft entries can be deleted');
        return;
      }
      const { error: deleteError } = await supabase?.from('time_entries')?.delete()?.eq('id', entryId)?.eq('entry_status', 'draft');
      if (deleteError) throw deleteError;
      setSuccessMessage('Time entry deleted');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchTimeEntries();
    } catch (err) {
      setError(err?.message || 'Failed to delete entry');
    }
  };

  const handleEditEntry = (entry) => {
    if (entry?.entry_status !== 'draft') {
      setError('Only draft entries can be edited');
      return;
    }
    setEditingEntry(entry);
    setActiveTab('manual');
  };

  const totalBillableToday = timeEntries?.filter(e => {
      const today = new Date()?.toDateString();
      return e?.billable_flag && new Date(e.created_at)?.toDateString() === today;
    })?.reduce((sum, e) => sum + (e?.billable_amount || 0), 0);

  const totalHoursToday = timeEntries?.filter(e => new Date(e.created_at)?.toDateString() === new Date()?.toDateString())?.reduce((sum, e) => sum + (e?.duration_minutes || 0), 0);

  const draftCount = timeEntries?.filter(e => e?.entry_status === 'draft')?.length;

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto md:ml-sidebar">
        {/* Header */}
        <div className="bg-surface border-b border-border px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading-semibold text-text-primary">Time Tracking</h1>
              <p className="text-sm text-text-secondary mt-0.5">Track billable hours and manage time entries</p>
            </div>
            <div className="flex items-center gap-3">
              {lawyerRate && (
                <div className="hidden md:flex items-center gap-2 bg-primary-50 text-primary px-3 py-1.5 rounded-lg text-sm font-body-medium">
                  <Icon name="DollarSign" size={14} />
                  <span>KES {Number(lawyerRate?.base_hourly_rate)?.toLocaleString()}/hr</span>
                </div>
              )}
              <button
                onClick={() => { setEditingEntry(null); setActiveTab('manual'); }}
                className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-body-medium hover:bg-primary-700 transition-colors"
              >
                <Icon name="Plus" size={16} />
                <span className="hidden sm:inline">Manual Entry</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-4 flex items-center gap-2 bg-success-50 text-success-700 border border-success-200 px-4 py-3 rounded-lg">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm">{successMessage}</span>
            </div>
          )}
          {error && (
            <div className="mb-4 flex items-center gap-2 bg-error-50 text-error-700 border border-error-200 px-4 py-3 rounded-lg">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto">
                <Icon name="X" size={14} />
              </button>
            </div>
          )}

          {/* Summary Cards */}
          <TimeEntrySummary
            totalHoursToday={totalHoursToday}
            totalBillableToday={totalBillableToday}
            draftCount={draftCount}
            totalEntries={timeEntries?.length}
          />

          {/* Tabs */}
          <div className="flex gap-1 bg-secondary-50 p-1 rounded-xl mb-6 w-fit">
            {[
              { id: 'timer', label: 'Stopwatch', icon: 'Timer' },
              { id: 'manual', label: 'Manual Entry', icon: 'Edit3' },
              { id: 'entries', label: `Entries (${timeEntries?.length})`, icon: 'List' }
            ]?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body-medium transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-surface text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name={tab?.icon} size={15} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'timer' && (
            <StopwatchTimer
              onSave={handleTimerSave}
              defaultRate={lawyerRate?.base_hourly_rate || 0}
            />
          )}
          {activeTab === 'manual' && (
            <ManualTimeEntry
              onSave={handleManualSave}
              onCancel={() => { setEditingEntry(null); setActiveTab('entries'); }}
              editingEntry={editingEntry}
              defaultRate={lawyerRate?.base_hourly_rate || 0}
            />
          )}
          {activeTab === 'entries' && (
            <TimeEntriesList
              entries={timeEntries}
              loading={loading}
              filterCase={filterCase}
              filterDate={filterDate}
              filterStatus={filterStatus}
              onFilterCase={setFilterCase}
              onFilterDate={setFilterDate}
              onFilterStatus={setFilterStatus}
              onEdit={handleEditEntry}
              onDelete={handleDeleteEntry}
              onRefresh={fetchTimeEntries}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default TimeTrackingDashboard;
